/**
 * Field-by-field view of an edit: what each changed field was, and what it becomes. Lists show
 * which items were added and removed; long text shows a word-level diff; covers sit side by side.
 */
import { Link } from 'react-router'
import { ArrowRight } from 'lucide-react'
import { coverUrl } from '../lib/api'
import { listDiff, wordDiff } from '../lib/diff'
import { categoryLabel, fieldLabel, languageName, ROLE_LABELS } from '../lib/format'
import { useQuery } from '../lib/query'
import type { Attribution, Credit, EntityType, Fingerprint, Link as LinkT, Ref } from '../../shared/model'

type Doc = Record<string, unknown> | null

/** One list item's text, per field, for list diffs. */
function itemText(field: string, v: unknown): string {
  if (typeof v === 'string') return v
  if (field === 'credits') {
    const c = v as Credit
    return `${ROLE_LABELS[c.role] ?? c.role}: ${c.creator.name}`
  }
  if (field === 'urls' || field === 'character_builder_urls') {
    const l = v as LinkT
    return l.label ? `${l.label} (${l.url})` : l.url
  }
  if (field === 'attributions') {
    const a = v as Attribution
    const what = a.fields.map((f) => (f === 'tags' ? 'tags' : 'description')).join(' and ')
    return `${what.charAt(0).toUpperCase()}${what.slice(1)} from ${a.source} (${a.url})`
  }
  if (field === 'fingerprints') {
    const f = v as Fingerprint
    return `${f.algo} ${f.hash.slice(0, 16)}…${f.size ? ` (${f.size} bytes)` : ''}`
  }
  if (v && typeof v === 'object' && 'name' in v) return (v as Ref).name
  return JSON.stringify(v)
}

function RecordName({ type, id }: { type: EntityType; id: string }) {
  const path = type === 'system' ? 'systems' : type === 'book' ? 'books' : 'publishers'
  const { data } = useQuery<{ doc: { name?: string; title?: string } }>(`/api/v1/${path}/${id}`)
  return <Link to={`/${path}/${id}`}>{data?.doc.name ?? data?.doc.title ?? 'Linked record'}</Link>
}

function Scalar({ field, value, type }: { field: string; value: unknown; type: EntityType }) {
  if (value === null || value === undefined || value === '') return <span className="diff-empty">empty</span>
  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>
  if (field === 'category') return <>{categoryLabel(String(value))}</>
  if (field === 'language') return <>{languageName(String(value))}</>
  if (field === 'parent_id')
    return <RecordName type={type === 'publisher' ? 'publisher' : 'system'} id={String(value)} />
  if (field === 'variant_of') return <RecordName type="book" id={String(value)} />
  if (field === 'container_kind') return <>{String(value) || 'None'}</>
  return <>{String(value)}</>
}

function FieldDiff({
  field,
  before,
  after,
  type,
}: {
  field: string
  before: unknown
  after: unknown
  type: EntityType
}) {
  if (field === 'cover') {
    return (
      <div className="diff-covers">
        {[before, after].map((v, i) => (
          <figure key={i} className={i ? 'is-add' : 'is-del'}>
            {v ? (
              <img src={coverUrl(v as string)!} alt={i ? 'New cover' : 'Previous cover'} />
            ) : (
              <span className="diff-empty">No cover</span>
            )}
            <figcaption>{i ? 'After' : 'Before'}</figcaption>
          </figure>
        ))}
      </div>
    )
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    const parts = listDiff(
      ((before as unknown[]) ?? []).map((v) => itemText(field, v)),
      ((after as unknown[]) ?? []).map((v) => itemText(field, v)),
    )
    if (!parts.length) return <span className="diff-empty">empty</span>
    return (
      <ul className="diff-list">
        {parts.map((p) => (
          <li key={`${p.kind}:${p.value}`} className={`diff-item is-${p.kind}`}>
            {p.kind !== 'same' && <span className="sr-only">{p.kind === 'add' ? 'Added: ' : 'Removed: '}</span>}
            {p.value}
          </li>
        ))}
      </ul>
    )
  }
  if (typeof before === 'string' && typeof after === 'string' && (before.length > 80 || after.length > 80)) {
    return (
      <p className="diff-text">
        {wordDiff(before, after).map((part, i) =>
          part.kind === 'same' ? (
            <span key={i}>{part.text}</span>
          ) : part.kind === 'add' ? (
            <ins key={i}>{part.text}</ins>
          ) : (
            <del key={i}>{part.text}</del>
          ),
        )}
      </p>
    )
  }
  return (
    <div className="diff-scalar">
      <del>
        <Scalar field={field} value={before} type={type} />
      </del>
      <ArrowRight className="diff-arrow" size={16} aria-label="becomes" />
      <ins>
        <Scalar field={field} value={after} type={type} />
      </ins>
    </div>
  )
}

export function Diff({
  type,
  before,
  after,
  fields,
  conflicts = [],
}: {
  type: EntityType
  before: Doc
  after: Doc
  fields: string[]
  conflicts?: string[]
}) {
  const shown = fields.length ? fields : Object.keys(after ?? before ?? {})
  const b = before ?? {}
  const a = after ?? {}
  return (
    <dl className="diff">
      {shown.map((f) => (
        <div key={f} className={`diff-row${conflicts.includes(f) ? ' is-conflict' : ''}`}>
          <dt>
            {fieldLabel(f)}
            {conflicts.includes(f) && <span className="diff-conflict">changed since</span>}
          </dt>
          <dd>
            {before === null ? (
              <FieldDiff field={f} before={Array.isArray(a[f]) ? [] : null} after={a[f]} type={type} />
            ) : (
              <FieldDiff field={f} before={b[f]} after={a[f]} type={type} />
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
